<template>
  <article class="import-approvals-wrapper">
    <notification />
    <section class="import-approvals-container">
      <header-simple :title="title" />
      <stepper :steps="steps" :current="currentStep" />
      <hr class="divider" height="1px" width="90%" />
      <div class="import-approvals-step__container">
        <div class="import-approvals-step step-0" v-if="currentStep == 0">
          <div class="import-approvals-result">
            <div class="import-approvals-result__header">
              <h3 class="import-approvals-result__title icon-file-excel">
                Step 1: Prepare your file
              </h3>
            </div>
            <div class="import-approvals-result__content">
              <p>
                To import your approvals, format your Excel file (xlsx) using the following names
                for your columns and formats for your cells:
              </p>
              <div>
                <p class="import-approvals-file-type-header">General Approval File Example</p>
                <import-format-table :headers="columnsFormat.headers" :body="columnsFormat.body">
                  <template v-slot:caption>{{ '' }}</template>
                </import-format-table>
              </div>
              <div v-if="showMultiStatusApproval">
                <p class="import-approvals-file-type-header">UBS None Fund and LCSC Approval File Example</p>
                <import-format-table :headers="multiStatusColumnsFormat.headers" :body="multiStatusColumnsFormat.body">
                  <template v-slot:caption>{{ '' }}</template>
                </import-format-table>
              </div>
            </div>
            <div class="import-approvals-result__header">
              <h3 class="import-approvals-result__title icon-file-add">
                Step 2: Upload your formatted file
              </h3>
            </div>
            <div class="import-approvals-result__content">
              <div class="import-approvals__file-container">
                <form action="" enctype="multipart/form-data" method="post" class="mds-form">
                  <div v-if="showMultiStatusApproval">
                    <mds-radio-button
                      class="import-approvals-file-type"
                      name="approval-type"
                      v-model="importApprovalFileType"
                      :value="IMPORT_TYPE.GENERAL_TYPE"
                    >
                      General Approval File
                    </mds-radio-button>
                    <mds-radio-button
                      class="import-approvals-file-type"
                      name="approval-type"
                      v-model="importApprovalFileType"
                      :value="IMPORT_TYPE.MULTI_STATUS_TYPE"
                    >
                      Multi-Status Fund File
                    </mds-radio-button>
                  </div>
                  <div class="mds-form__field-group">
                    <input
                      class="mds-form__input import-approvals__file-input"
                      type="file"
                      id="import-approvals-input"
                      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      @change="onSelectFile"
                      ref="file"
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div class="actions-bar">
            <div class="box left">
              <button class="mds-button mds-button--secondary" type="button" @click="cancelImport">
                Cancel
              </button>
            </div>
            <div class="box right">
              <button
                class="mds-button mds-button--primary approvals-button__start-file-validation"
                type="button"
                :disabled="!canContinue"
                @click="startValidation"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
        <div class="import-approvals-step step-1" v-if="currentStep == 1">
          <template v-if="!this.error">
            <loading-spinner />
            <h3 class="import-approvals-step__title centered">Checking {{ fileName }}</h3>
          </template>
          <template v-else>
            <h3 v-if="hasValidationFailed" class="step-1-title import-approvals-step__title">
              We found a few problems and were unable to import your funds.<br />
              Please review issues below and update your spreadsheet.
            </h3>
            <h3 v-else class="step-1-title import-approvals-step__title">
              Our check is complete. Please review the items below.
            </h3>

            <div class="import-approvals-result" v-if="isInvalidExtensionCheck">
              <div class="import-approvals-result__header">
                <h3 class="import-approvals-result__title icon-not-ready">
                  Your spreadsheet has an extension issue. Only *.xlsx files allowed.
                </h3>
              </div>
              <div class="import-approvals-result__content">
                <p>
                  Check the format of your Excel file with the proper extension (xlsx) to make sure
                  you are importing a valid file:
                </p>
                <import-format-table v-if="!isMultiStatusFileSelected"
                :headers="columnsFormat.headers" :body="columnsFormat.body">
                  <template v-slot:caption>{{ '' }}</template>
                </import-format-table>
                <import-format-table v-else
                :headers="multiStatusColumnsFormat.headers" :body="multiStatusColumnsFormat.body">
                  <template v-slot:caption>{{ '' }}</template>
                </import-format-table>
              </div>
            </div>

            <div
              class="import-approvals-result"
              v-if="hasValidationFailed && checkInvalidStatus(['InvalidData'])"
            >
              <div class="import-approvals-result__header">
                <h3 class="import-approvals-result__title icon-not-ready">
                  Approvals not ready for import
                </h3>
              </div>
              <div class="import-approvals-result__content">
                <p>Review the following error:</p>
                <p class="table-heading">
                  <strong>{{ errorMessage }}</strong>
                </p>
              </div>
            </div>

            <div class="actions-bar">
              <div class="box left">
                <button class="mds-button mds-button--flat" @click="startAgain">
                  <svg class="mds-icon mds-button__icon mds-button__icon--left" aria-hidden="true">
                    <use xlink:href="static/mds/icons/mds.svg#caret-left--s"></use>
                  </svg>
                  <span class="mds-button__text">
                    Back
                  </span>
                </button>
              </div>
            </div>
          </template>
        </div>

        <div class="import-approvals-step step-2" v-if="currentStep == 2">
          <h3 v-if="hasValidationFailed" class="step-2-title import-approvals-step__title">
            We found a few problems and were unable to import your funds.<br />
            Please review issues below and update your spreadsheet.
          </h3>
          <h3 v-else class="step-2-title import-approvals-step__title">
            Our check is complete. Please review the items below.
          </h3>

          <div
            class="import-approvals-result"
            v-if="hasValidationFailed && checkInvalidStatus(['InvalidFormat'])"
          >
            <div class="import-approvals-result__header">
              <h3 class="import-approvals-result__title icon-not-ready">
                Your spreadsheet has formatting issues
              </h3>
            </div>
            <div class="import-approvals-result__content">
              <p>
                Check the format of your Excel file (xlsx) to make sure you are using the following
                names for your columns and formats for your cells:
              </p>
              <import-format-table v-if="!isMultiStatusFileSelected"
              :headers="columnsFormat.headers" :body="columnsFormat.body">
                <template v-slot:caption>{{ '' }}</template>
              </import-format-table>
              <import-format-table v-else
                :headers="multiStatusColumnsFormat.headers" :body="multiStatusColumnsFormat.body">
                  <template v-slot:caption>{{ '' }}</template>
                </import-format-table>
            </div>
          </div>

          <div
            class="import-approvals-result"
            v-if="hasValidationFailed && checkInvalidStatus(['InvalidData'])"
          >
            <div class="import-approvals-result__header">
              <h3 class="import-approvals-result__title icon-not-ready">
                Approvals not ready for import
              </h3>
            </div>
            <div class="import-approvals-result__content">
              <p>Review the following error:</p>
              <p class="table-heading">
                <strong>{{ errorMessage }}</strong>
              </p>
            </div>
          </div>

          <!-- ERROR/FAILED -->
          <div
            class="import-approvals-result has-list-of-invalid-data failed-result"
            v-if="hasInvalidFunds && hasValidationFailed && !hasWarningFunds && !hasValidFunds"
          >
            <div class="import-approvals-result__header">
              <h3
                class="import-approvals-result__title import-approvals-result__title icon-not-ready"
              >
                Approvals not ready for import
              </h3>
            </div>
            <div class="import-approvals-result__content">
              <mds-button
                class="import-approvals-error-download"
                variation="primary"
                @click="downloadIssues(validationOutput.invalid)"
              >
                Export Issues
              </mds-button>
              <p class="view-funds-btn">
                <button
                  class="mds-button mds-button--flat approvals-button__toggle-details-error"
                  type="button"
                  @click="toggleDetails('invalid')"
                >
                  <svg class="mds-icon mds-button__icon mds-button__icon--left" aria-hidden="true">
                    <use :xlink:href="setTriangle(openDetails.invalid)">
                      <title>open/close indicator</title>
                    </use>
                  </svg>
                  <span class="mds-button__text toggle-btn__issues"> View Issues </span>
                </button>
              </p>
              <transition name="scale">
                <approvals-validation-table
                  v-show="openDetails.invalid"
                  type="invalid"
                  :approvals="validationOutput.invalid"
                />
              </transition>
            </div>
          </div>

          <!-- WARNING -->
          <div
            class="import-approvals-result warning-result"
            v-if="hasWarningFunds || (hasInvalidFunds && hasValidFunds)"
          >
            <div class="import-approvals-result__header">
              <h3
                :class="[
                  'import-approvals-result__title',
                  'import-approvals-result__title',
                  'icon-warning-funds'
                ]"
              >
                Approvals not ready for import
              </h3>
            </div>
            <div class="import-approvals-result__content import-approvals-result__warning">
              <mds-button
                class="import-approvals-warning-download"
                variation="primary"
                @click="downloadIssues(invalidAndWarningFunds)"
              >
                Export Issues
              </mds-button>
              <p>
                You can still import the file but these rows will not be included in your import:
              </p>
              <p class="view-funds-btn">
                <button
                  class="mds-button mds-button--flat approvals-button__toggle-details-warning"
                  type="button"
                  @click="toggleDetails('warning')"
                >
                  <svg class="mds-icon mds-button__icon mds-button__icon--left" aria-hidden="true">
                    <use :xlink:href="setTriangle(openDetails.warning)">
                      <title>open/close indicator</title>
                    </use>
                  </svg>
                  <span class="mds-button__text toggle-btn__issues"> View Issues </span>
                </button>
              </p>
              <transition name="scale">
                <approvals-validation-table
                  v-show="openDetails.warning"
                  type="warning"
                  :approvals="invalidAndWarningFunds"
                />
              </transition>
            </div>
          </div>

          <!-- SUCCESS -->
          <div
            class="import-approvals-result success-result"
            v-if="hasValidFunds && !hasInvalidFunds && !hasWarningFunds"
          >
            <div class="import-approvals-result__header">
              <h3 class="import-approvals-result__title import-approvals-result__title icon-ready">
                Approvals ready for import
              </h3>
            </div>
            <div class="import-approvals-result__content import-approvals-result__success">
              <p>Click continue to import {{ successFullApprovalsCount }} approvals</p>
            </div>
          </div>
          <div class="actions-bar">
            <div class="box left">
              <button
               class="mds-button mds-button--flat approvals-button__back-initial"
               @click="startAgain"
              >
                <svg class="mds-icon mds-button__icon mds-button__icon--left" aria-hidden="true">
                  <use xlink:href="static/mds/icons/mds.svg#caret-left--s"></use>
                </svg>
                <span class="mds-button__text">
                  Back
                </span>
              </button>
            </div>
            <div class="box right">
              <button
                v-if="validationStatus !== 'ValidationFailed'"
                class="mds-button mds-button--secondary approvals-button__cancel-import"
                type="button"
                @click="cancelImport"
              >
                Cancel
              </button>
              <mds-button  v-if="validationStatus === 'ValidationFailed'" class= "approvals-button__return-to-all-clients"
                variation="primary"
                @click="cancelImport"
              >
                Return to All Clients
              </mds-button>
              <mds-button  v-else class= "approvals-button__start-import"
                variation="primary"
                 @click="hasValidFunds && startImport(currentStep)"
              >
                Continue
              </mds-button>
            </div>
          </div>
        </div>

        <div class="import-approvals-step importing-step step-3" v-if="currentStep == 3">
          <h6 class="import-approvals-step__title centered">Importing {{ fileName }}</h6>
          <progress-bar
            :duration="totalValidateTime"
            :enabled="currentStep == 3"
            :complete="importCompleted"
          />
        </div>
      </div>
    </section>
    <notification />
    <modal />
  </article>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import {
  get,
  includes,
  reduce,
  concat,
  map,
} from 'lodash';

import MdsRadioButton from '@mds/radio-button';
import { cleanObj, exportExcel } from '@/utils/misc';
import { goToLogoutRoute, goToRoute } from '@/utils';
import { columnsFormat, steps, multiStatusColumnsFormat } from '@/config/import-approvals-format.json';

import ErrorNotificationMixin from '@/mixins/ErrorNotificationMixin';

/* eslint import/no-cycle: [2, { maxDepth: 1 }] */
import * as ApprovalsService from '@/services/Approvals';

import LoadingSpinner from '@/components/atoms/LoadingSpinner';
import HeaderSimple from '@/components/atoms/HeaderSimple';
import Notification from '@/components/atoms/Notification';
import Modal from '@/components/atoms/Modal';
import ProgressBar from '@/components/atoms/ProgressBar';
import Stepper from '@/components/atoms/Stepper';
import ImportFormatTable from '@/components/atoms/ImportFormatTable';
import ApprovalsValidationTable from '@/components/atoms/ApprovalsValidationTable';

const IMPORT_TYPE = {
  GENERAL_TYPE: 'general-approval-type',
  MULTI_STATUS_TYPE: 'multi-approval-type',
};
export default {
  name: 'import-approvals',
  components: {
    HeaderSimple,
    Stepper,
    LoadingSpinner,
    ApprovalsValidationTable,
    ProgressBar,
    Modal,
    ImportFormatTable,
    Notification,
    MdsRadioButton,
  },
  mixins: [
    ErrorNotificationMixin({ component: 'ImportApprovals' }),
  ],
  data() {
    return {
      title: 'Import Approvals',
      columnsFormat,
      multiStatusColumnsFormat,
      IMPORT_TYPE,
      steps,
      currentStep: 0,
      canContinue: false,
      processing: false,
      error: false,
      errorMessage: '',
      filePath: '',
      startValidateTime: 0,
      totalValidateTime: 0,
      validationOutput: {},
      validationStatus: '',
      invalidStatuses: [],
      openDetails: {
        invalid: false,
        warning: false,
      },
      file: null,
      fileName: '',
      importCompleted: false,
      request: null,
      requestController: null,
      requestUrl: null,
      toTheActualAPI: true,
      importApprovalFileType: IMPORT_TYPE.GENERAL_TYPE,
    };
  },
  computed: {
    ...mapState({
      showMultiStatusApproval: (state) => state.system.config.importApprovals.showMultiStatusApproval,
    }),
    isInvalidExtensionCheck() {
      return (
        this.validationStatus === 'ValidationFailed'
        && this.checkInvalidStatus(['InvalidExtension'])
      );
    },
    hasValidationFailed() {
      return this.validationStatus === 'ValidationFailed';
    },
    hasInvalidFunds() {
      return (
        Array.isArray(this.validationOutput.invalid) && this.validationOutput.invalid.length > 0
      );
    },
    hasWarningFunds() {
      return (
        Array.isArray(this.validationOutput.warnings) && this.validationOutput.warnings.length > 0
      );
    },
    hasValidFunds() {
      return Array.isArray(this.validationOutput.valid) && this.validationOutput.valid.length > 0;
    },
    successFullApprovalsCount() {
      return Array.isArray(this.validationOutput.valid) && this.validationOutput.valid.length;
    },
    invalidAndWarningFunds() {
      const invalid = get(this.validationOutput, 'invalid', []);
      const warning = get(this.validationOutput, 'warnings', []);
      return invalid.concat(warning);
    },
    isMultiStatusFileSelected() {
      return this.importApprovalFileType === IMPORT_TYPE.MULTI_STATUS_TYPE;
    },
  },
  methods: {
    ...mapActions(['logError']),
    cancelImport() {
      this.$route.name !== 'grid' && goToRoute(this.$route, this.$router, '/');
    },
    onSelectFile(event) {
      this.canContinue = true;
      this.filePath = '';
      [this.file] = event.target.files;
      if (!this.validateFile(event.target)) {
        this.canContinue = false;
        this.error = true;
        this.filePath = event.target.value;
        this.currentStep = 1;
        this.validationStatus = 'ValidationFailed';
        this.invalidStatuses.push('InvalidExtension');
      }
    },
    validateFile(fileObj) {
      const isNotEmpty = fileObj.value !== '';
      const isOnlyOneFile = fileObj.files && fileObj.files.length === 1;
      const extension = fileObj.value.split('.').pop();
      const hasAllowedExtensionFile = ['xlsx'].indexOf(extension) >= 0;
      return isNotEmpty && isOnlyOneFile && hasAllowedExtensionFile;
    },
    toggleDetails(section) {
      this.openDetails[section] = !this.openDetails[section];
    },
    async startValidation() {
      const file = get(this.$refs, 'file.files[0]', {});
      const { isMultiStatusFileSelected } = this;
      this.fileName = file.name;
      this.currentStep = 1;
      this.startValidateTime = new Date().getTime();

      ApprovalsService.validateFundApprovalsFile({
        file, isMultiStatusFileSelected,
      }).then((response) => {
        this.error = false;
        this.errorMessage = '';
        this.validationOutput = get(response, 'data', {});
        this.validationStatus = get(this.validationOutput, 'status', 'ValidationFailed');
        this.currentStep = 2;

        if (!response.status.success) {
          // General Error and the error message will be inside the response.status object
          this.error = true;
          this.errorMessage = response.status.message;
          this.invalidStatuses = ['InvalidData'];
          this.validationOutput = {
            ...this.validationOutput,
            invalid: map(this.validationOutput.invalid, (invalidData, index) => ({
              ...invalidData,
              secId: invalidData.secId || `invalid-${index}`,
            })),
          };
        } else {
          // There could be one or more errors but more related to a specific row of the file
          this.invalidStatuses = [
            ...new Set(
              reduce(
                this.validationOutput.invalid,
                (list, item) => concat(list, item.errors),
                [],
              ),
            ),
          ];
        }
        this.totalValidateTime = Math.ceil(
          (new Date().getTime() - this.startValidateTime) / 1000,
        );
      }).catch((error) => {
        this.error = true;
        this.errorMessage = error.message;
        this.validationStatus = 'ValidationFailed';
        this.invalidStatuses = ['InvalidData'];
        this.logError({
          errorFrom: 'ImportApprovals:methods:startValidation',
          details: {
            ...Object.getOwnPropertyDescriptors(error),
            message: error.message,
          },
        });
      });
    },
    checkInvalidStatus(value) {
      let invalid = false;
      map(value, (v) => {
        if (includes(this.invalidStatuses, v)) {
          invalid = true;
        }
      });
      return invalid;
    },
    startAgain() {
      this.filePath = '';
      this.currentStep = 0;
      this.invalidStatuses = [];
      this.error = false;
      this.importCompleted = false;
      this.validationStatus = '';
      this.validationOutput = {};
      this.openDetails = {
        invalid: false,
        warning: false,
      };
    },
    startImport() {
      const payload = this.validationOutput.valid.map(cleanObj);

      this.currentStep = 3;
      this.processing = true;
      this.startValidateTime = new Date().getTime();
      ApprovalsService.importFundApprovals({
        payload,
      }).then((result) => {
        this.processing = false;
        this.error = false;
        this.totalValidateTime = Math.ceil(
          (new Date().getTime() - this.startValidateTime) / 1000,
        );
        this.importCompleted = true;
        this.$router.push('/', () => {
          this.$store.dispatch('openNotification', {
            title: 'Import of approvals complete.',
            action: 'pending',
            actionLabel: 'View updates in Pending',
          });
        });
        return result;
      }).catch((error) => {
        this.error = true;
        this.currentStep = 2;
        this.loading = false;
        const httpStatus = (error?.response.status) ? (error?.response.status) : (error?.status);
        if (httpStatus === 401) {
          goToLogoutRoute(this.$route, this.$router);
        } else if (error.name !== 'AbortError') {
          this.notifyError({
            error,
            section: 'methods:startImport',
            data: { url: this.requestUrl },
            notificaction: {
              title: 'Importing Error',
              message: 'Import of approvals process has pesented an error.',
            },
          });
        }
      });
      return true;
    },
    setTriangle(value) {
      if (value === false) {
        return 'static/mds/icons/mds.svg#triangle-fill-right';
      }
      return 'static/mds/icons/mds.svg#triangle-fill-down';
    },
    getErrorDataForExcel(errorData) {
      const errorJson = errorData.map((item) => {
        return {
          Issues: item.messages[0],
        };
      });
      return errorJson;
    },
    downloadIssues(errorData) {
      const jsonData = this.getErrorDataForExcel(errorData);
      exportExcel(jsonData, 'Import_Approval_Issues');
    },
  },
  beforeDestroy() {
    this.requestController && this.requestController.abort();
  },
};
</script>

<style lang="scss">
.import-approvals-container {
  display: flex;
  flex-direction: column;
}
.import-approvals-wrapper {
  width: calc(100% - 85px);
}
.import-approvals-step {
  &__container {
    height: 100%;
    overflow-x: none;
    overflow-y: auto;
    padding: $mds-space-5-x $mds-space-8-x $mds-space-2-x $mds-space-8-x;
  }
  &__title {
    @include mds-level-4-heading();
  }
  .box {
    display: flex;
    justify-content: space-between;
    margin-top: $mds-space-8-x;
  }
  .centered {
    text-align: center;
  }
  p {
    @include mds-body-text-m();
  }
}
.import-approvals {
  &__check-list {
    font-family: $mds-font-family-sans-serif;
    font-size: $mds-font-size-m;
    font-weight: $mds-font-weight-thin;
    list-style: none;
    padding: 0;

    .check-list__item {
      background: url("/static/assets/check_icon_ct.svg") no-repeat left center;
      margin-bottom: $mds-space-inset-4-x;
      min-height: $mds-space-4-x;
      padding-right: $mds-space-inset-4-x;
      padding-left: 48px; // Match to deisgn
    }
  }
  &__file-container {
    background-color: $mds-background-color-light-gray;
    margin: $mds-space-4-x 0;
    padding: $mds-space-8-x;

    .import-approvals-file-type {
      display: inline-block;
      margin-right: $mds-space-4-x;
    }
  }
  &__file-input.mds-form__input {
    background-color: $mds-background-color-light-gray;
    box-shadow: none;
    height: 42px; // Match to design
    margin-top: $mds-space-2-x;
  }
  &__file-input.mds-form__input::-webkit-file-upload-button {
    border: 0;
    border-radius: $mds-space-inset-3-x;
    box-shadow: inset 0 0 0 1px $mds-interactive-color-primary-default;
    background-color: $mds-interactive-color-primary-default;
    color: $mds-color-white;
    padding: $mds-space-1-x $mds-space-2-x;
  }
}
.import-approvals-result {
  min-height: calc(100vh - 620px);
  &__header {
    display: inline-block;
  }
  &__title {
    display: inline-block;
    @include mds-level-4-heading();
    font-weight: $mds-font-weight-bold;
    padding: $mds-space-inset-1-x 48px; // Match to design
    margin-bottom: 0;
  }
  &__title.icon-not-ready {
    background: url("/static/assets/not_ready_icon_ct.svg") no-repeat left center;
  }
  &__title.icon-warning-funds {
    background: url("/static/assets/yellow_alert.svg") no-repeat left center;
  }
  &__title.icon-ready {
    background: url("/static/assets/check_icon_ct.svg") no-repeat left center;
  }
  &__title.icon-file-excel {
    background: url("/static/assets/file_excel_circle.svg") no-repeat left center;
  }
  &__title.icon-file-add {
    background: url("/static/assets/add_file_circle.svg") no-repeat left center;
  }
  &__content {
    border-bottom: 1px solid $mds-color-neutral-90;
    font-family: $mds-font-family-sans-serif;
    font-size: $mds-font-size-m;
    font-weight: $mds-font-weight-thin;
    padding-left: 48px; //Match design
    .table-heading {
      margin: $mds-space-inset-4-x 0;
    }
  }
  .import-approvals-result__content {
    position: relative;

    .import-approvals-file-type-header {
      font-weight: bold;
    }
  }
  .import-approvals-error-download {
    position: absolute;
    right: 0;
    top: 20px;
  }
  .import-approvals-warning-download {
    float: right;
  }
}
.importing-step {
  .import-approvals-step {
    &__title {
      @include mds-level-6-heading();
      font-size: $mds-font-size-m;
      font-weight: $mds-font-weight-thin;
      margin: $mds-space-stack-4-x;
    }
  }
}
.divider {
  border-top: $mds-border-separator-on-white;
}
.view-funds-btn {
  border-top: $mds-border-separator-on-white;
  padding: $mds-space-inset-2-x 0;
}
.actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-bottom: $mds-space-2-x;
  margin-top: $mds-space-4-x;
  button {
    margin-left: $mds-space-inset-2-x;
  }
}
.guidelines-container {
  @include mds-body-text-s();
  padding: $mds-space-inset-3-x;
  border-color: $mds-feedback-color-success;
  border-style: solid;
  border-width: 1px;
}
.alternate-color-table {
  tr td:first-child,
  tr td:last-child {
    padding: $mds-space-inset-2-x;
  }
  tr:nth-child(odd) td {
    background-color: $mds-color-neutral-95;
  }
}
.warning-table {
  margin-bottom: $mds-space-inset-2-x;
  thead th {
    font-weight: $mds-font-weight-bold;
    padding: $mds-space-inset-2-x 0;
  }
  tbody td.warning-body-cell {
    border: 0;
    padding: $mds-space-inset-2-x 0;
    &:first-child {
      padding-left: $mds-space-inset-2-x;
    }
  }
}
.unknown-table {
  margin-bottom: $mds-space-inset-4-x;
}
.toggle-btn__issues {
  font-weight: $mds-font-weight-bold;
}
</style>
